import React from "react";
import { AiSparkleIcon } from "../../atoms/AiSparkleIcon";
import { SoonSlot } from "../../atoms/SoonSlot";
import { FormField } from "../FormField";
import { StorySourcesSection } from "../StorySourcesSection";
import { Sections } from "./StoryDetailsFields.styles";
import {
  NO_MODEL_LABEL,
  type StoryDetailsFieldsProps,
} from "./StoryDetailsFields.types";

/** Figma draws the Goal label's "Logo" sparkle at 16. */
const GOAL_ICON_SIZE = 16;

export const StoryDetailsFields: React.FC<StoryDetailsFieldsProps> = ({
  values,
  onChange,
  context,
  children,
}) => {
  const availableSources = context?.availableSources ?? [];
  const modelOptions = context?.modelOptions ?? [];

  const allSourcesSelected =
    availableSources.length > 0 &&
    availableSources.every((s) => values.selectedSourceIds.includes(s.id));

  const selectedModelLabel =
    modelOptions.find((m) => m.id === values.selectedModelId)?.label ??
    NO_MODEL_LABEL;

  const sourcesSection = (
    <StorySourcesSection
      variant="modal"
      isPending={context?.isSourcesLoading}
      selectedSource={values.sourceKind}
      onSourceToggle={(sourceKind) =>
        // Switching tab closes whatever the other tab had open, so returning
        // to it does not flash a stale popover.
        onChange({ sourceKind, isAddingSource: false, isModelOpen: false })
      }
      availableSources={availableSources}
      selectedSourceIds={values.selectedSourceIds}
      isAddingSource={values.isAddingSource}
      onToggleAddingSource={() =>
        onChange({ isAddingSource: !values.isAddingSource })
      }
      sourceDropdownRef={context?.popoverBoundaryRef}
      allSourcesSelected={allSourcesSelected}
      onSourceSelect={(id) =>
        onChange({ selectedSourceIds: [...values.selectedSourceIds, id] })
      }
      onSourceDeselect={(id) =>
        onChange({
          selectedSourceIds: values.selectedSourceIds.filter(
            (sourceId) => sourceId !== id,
          ),
        })
      }
      onSelectAllSources={() =>
        onChange({ selectedSourceIds: availableSources.map((s) => s.id) })
      }
      onDeselectAllSources={() => onChange({ selectedSourceIds: [] })}
      selectedModel={values.selectedModelId}
      selectedModelLabel={selectedModelLabel}
      modelOptions={modelOptions}
      onModelSelect={(selectedModelId) => onChange({ selectedModelId })}
      isModelOpen={values.isModelOpen}
      onToggleModelOpen={() =>
        onChange({ isModelOpen: !values.isModelOpen })
      }
      modelDropdownRef={context?.popoverBoundaryRef}
    />
  );

  return (
    <Sections>
      <FormField
        label="Goal"
        labelIcon={<AiSparkleIcon size={GOAL_ICON_SIZE} />}
        surface="plain"
        placeholder="Add goal of your story"
        value={values.goal}
        onChange={(e) => onChange({ goal: e.target.value })}
      />

      {children}

      {context?.sourcesFromModel ? (
        // A Story on the new path reads the sources already in the model; picking them is not built yet.
        <SoonSlot block>{sourcesSection}</SoonSlot>
      ) : (
        sourcesSection
      )}
    </Sections>
  );
};

export default StoryDetailsFields;
